// What the TimeBlocker screen keeps while it is open, outside the URL: the day
// shown, the one line of failure, what is being carried from the list to the
// timeline, which Case or Action was pressed in the list (its blocks light up
// on the timeline for a moment), and which block's detail is open. The list and the timeline share it.
import {
  createContext,
  type ReactNode,
  type RefObject,
  useContext,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { todayIn } from "../cases/dates";
import { useTimeZone } from "../me/profile";
import { useMinuteClock } from "./clock";
import { dayStartIn } from "./day";
import type { DayPlace } from "./queries";

/** An Action carried from the list (pointer position, and the minute under it on the timeline). */
export interface Carry {
  item: {
    action: {
      caseId: string;
      caseTitle: string;
      id: string | null;
      title: string;
    };
  };
  caseOnly?: boolean;
  x: number;
  y: number;
  /** Where the block would start; null when the pointer is not over the own lane. */
  minute: number | null;
  /** The block under the pointer (letting go puts the card into it), if any. */
  overBlock: string | null;
}

/** A Case (no step) or an Action pressed in the list. */
export interface FocusTarget {
  caseId: string;
  stepId: string | null;
}

/** How long the blocks of a pressed card stay lit. */
const FOCUS_MS = 1800;

export interface TimeBlockerScreen extends DayPlace {
  /** The day shown and the time zone, as a write takes them. */
  place: DayPlace;
  today: string;
  now: Date;
  /** The day's midnight in the time zone (ms). */
  dayStart: number;
  setDay(day: string): void;
  error: string | null;
  setError(text: string | null): void;
  /** What is carried from the list (its own store: a pointer move redraws only what follows it). */
  carry: CarryStore;
  /** Gives the minute under a point of the own lane (registered by the timeline). */
  hitMinute: RefObject<((x: number, y: number) => number | null) | null>;
  /** Scrolls the timeline near its edges while something is carried. */
  edgeScroll: RefObject<((y: number) => void) | null>;
  /** The Case or Action pressed in the list: the blocks linked to it are lit for a moment. */
  focus: FocusTarget | null;
  setFocus(target: FocusTarget): void;
  /** The block whose detail is open. */
  selected: string | null;
  setSelected(id: string | null): void;
}

export interface CarryStore {
  get(): Carry | null;
  set(carry: Carry | null): void;
  subscribe(onChange: () => void): () => void;
}

function createCarryStore(): CarryStore {
  let current: Carry | null = null;
  const subs = new Set<() => void>();
  return {
    get: () => current,
    set(next) {
      current = next;
      for (const fn of subs) fn();
    },
    subscribe(fn) {
      subs.add(fn);
      return () => subs.delete(fn);
    },
  };
}

/** The block under a point of the screen, if any (what a carried card would go into). */
export function blockAt(x: number, y: number): string | null {
  return (
    document
      .elementFromPoint(x, y)
      ?.closest("[data-block-id]")
      ?.getAttribute("data-block-id") ?? null
  );
}

/** Whether a carried card is over this block now (redraws only when that changes). */
export function useIsCarriedOver(blockId: string): boolean {
  const { carry } = useTimeBlockerScreen();
  return useSyncExternalStore(
    carry.subscribe,
    () => carry.get()?.overBlock === blockId,
    () => false,
  );
}

/** Whether anything is carried (redraws only when that changes). */
export function useIsCarrying(): boolean {
  const { carry } = useTimeBlockerScreen();
  return useSyncExternalStore(
    carry.subscribe,
    () => carry.get() !== null,
    () => false,
  );
}

/** What is carried now (redraws on every move of it). */
export function useCarry(): Carry | null {
  const { carry } = useTimeBlockerScreen();
  return useSyncExternalStore(carry.subscribe, carry.get, carry.get);
}

const Context = createContext<TimeBlockerScreen | null>(null);

export function TimeBlockerScreenProvider({
  children,
}: {
  children: ReactNode;
}) {
  const timeZone = useTimeZone();
  const now = useMinuteClock();
  const today = todayIn(timeZone, now);
  // The day shown starts as today and stays when midnight passes ("Today"
  // can be pressed then).
  const [shown, setDay] = useState(today);
  const [error, setError] = useState<string | null>(null);
  const [carry] = useState(createCarryStore);
  const [focus, showFocus] = useState<FocusTarget | null>(null);
  const focusTimer = useRef(0);
  const [selected, setSelected] = useState<string | null>(null);
  const hitMinute = useRef<((x: number, y: number) => number | null) | null>(
    null,
  );
  const edgeScroll = useRef<((y: number) => void) | null>(null);
  const value = useMemo<TimeBlockerScreen>(
    () => ({
      day: shown,
      timeZone,
      place: { day: shown, timeZone },
      today,
      now,
      dayStart: dayStartIn(shown, timeZone),
      setDay: (next) => {
        setDay(next);
        setError(null);
        setSelected(null);
      },
      error,
      setError,
      carry,
      hitMinute,
      edgeScroll,
      focus,
      setFocus: (target) => {
        window.clearTimeout(focusTimer.current);
        showFocus(target);
        focusTimer.current = window.setTimeout(() => showFocus(null), FOCUS_MS);
      },
      selected,
      setSelected,
    }),
    [shown, timeZone, today, now, error, carry, focus, selected],
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useTimeBlockerScreen(): TimeBlockerScreen {
  const v = useContext(Context);
  if (!v)
    throw new Error("useTimeBlockerScreen outside the TimeBlocker screen");
  return v;
}
